/**
 * AudienceContextBanner — Bandeau contextuel d'adaptation par audience.
 *
 * Affiche, en haut d'une page (sous le hero ou la nav), un message court
 * qui aide l'utilisateur à comprendre comment LIRE la page en fonction de
 * son audience active (eleve / enseignant / futur-enseignant).
 *
 * Pourquoi ?
 * Plusieurs pages (CCS, REMC, simulateur, lexique, etc.) sont consultées
 * par 2 audiences différentes mais avec des intentions distinctes :
 *   - Enseignant certifié → "comment faire travailler mes élèves"
 *   - Futur enseignant     → "comment je révise pour le jury"
 *
 * Plutôt que de dupliquer les pages, on ajoute un bandeau de contexte qui
 * recadre l'usage. C'est moins coûteux, plus maintenable, et ça respecte
 * la séparation stricte des audiences (les liens proposés sont toujours
 * adaptés à l'audience active).
 *
 * Usage minimal :
 *
 *   <AudienceContextBanner
 *     contexts={{
 *       enseignant: {
 *         title: "Pour faire travailler tes élèves",
 *         bullets: ["Anime une étude de cas en classe", "Crée des fiches"],
 *       },
 *       'futur-enseignant': {
 *         title: "Pour réussir ton oral CCS",
 *         bullets: ["Mémorise les 27 cas", "Prépare la posture jury"],
 *       },
 *     }}
 *   />
 *
 * Si l'utilisateur n'a pas encore choisi d'audience, le bandeau propose
 * d'en choisir une (lien vers la home / sélecteur d'audience).
 *
 * Si l'audience active n'a pas de configuration (`contexts[audience]`),
 * le composant ne s'affiche pas (silent fallback).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, GraduationCap, BookOpen, Compass, ArrowRight, Info,
} from 'lucide-react';
import useAudience, { AUDIENCES, AUDIENCE_META } from '../hooks/useAudience';
import { useApp } from '../contexts/AppContext';

// ── Mapping audience → icône / accent (par défaut, peut être surchargé) ─
const AUDIENCE_VISUAL = {
  [AUDIENCES.ELEVE]: {
    icon: Sparkles,
    accent: '#cf2031',
    label: 'Vue Élève',
  },
  [AUDIENCES.ENSEIGNANT]: {
    icon: BookOpen,
    accent: '#0891b2',
    label: 'Vue Enseignant certifié',
  },
  [AUDIENCES.FUTUR_ENSEIGNANT]: {
    icon: GraduationCap,
    accent: '#ea580c',
    label: 'Vue Futur enseignant CCS',
  },
};

/**
 * Bandeau "choisis ton audience" affiché quand aucune audience n'est encore
 * sélectionnée. Lien vers la page d'accueil qui contient le sélecteur.
 */
function NoAudienceBanner({ pageHint }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 sm:p-5 my-4"
      data-testid="audience-banner-empty"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #cf2031 0%, #0891b2 50%, #ea580c 100%)' }}
        >
          <Compass className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Avant de commencer
          </p>
          <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
            Choisis ton profil pour adapter le contenu
          </h3>
          {pageHint && (
            <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-snug">
              {pageHint}
            </p>
          )}
          <Link
            to="/#audience"
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider mt-2 text-slate-900 hover:text-slate-700 transition-colors"
            data-testid="audience-banner-empty-cta"
          >
            Choisir mon profil <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Bandeau principal contextualisé pour l'audience active.
 *
 * Props :
 *  - contexts : { [audience]: { title, bullets?, intro?, cta?, ctaTo? } }
 *  - showOnEleve : si false (défaut), le bandeau ne s'affiche pas pour les
 *      élèves quand la page n'est pas faite pour eux. Permet d'éviter une
 *      pollution visuelle sur les pages où l'élève ne devrait pas atterrir.
 *  - compact : version plus dense (1 ligne)
 *  - pageHint : message court affiché quand l'audience n'est pas définie
 */
export default function AudienceContextBanner({
  contexts = {},
  showOnEleve = true,
  compact = false,
  pageHint,
  className = '',
  hideOnEmpty = false,
}) {
  const { audience: storedAudience } = useAudience();
  const { user } = useApp();

  /**
   * Audience effective = user.role connecté > choix localStorage.
   * Si l'utilisateur est authentifié comme enseignant/admin, on n'affiche
   * JAMAIS le bandeau "Choisis ton profil" — il EST enseignant.
   * Idem si user.role === 'eleve'.
   */
  const audience = React.useMemo(() => {
    if (user?.role === 'enseignant' || user?.role === 'admin') {
      return AUDIENCES.ENSEIGNANT;
    }
    if (user?.role === 'eleve') {
      return AUDIENCES.ELEVE;
    }
    return storedAudience;
  }, [user?.role, storedAudience]);

  // Pas d'audience → soit on masque (hideOnEmpty), soit on invite à choisir
  if (!audience) {
    if (hideOnEmpty) return null;
    return <NoAudienceBanner pageHint={pageHint} />;
  }

  // L'audience active n'a pas de configuration sur cette page → silent
  const cfg = contexts[audience];
  if (!cfg) return null;

  // Audience ELEVE : on respecte la flag showOnEleve (par défaut affiché)
  if (audience === AUDIENCES.ELEVE && !showOnEleve) return null;

  const visual = AUDIENCE_VISUAL[audience] || AUDIENCE_VISUAL[AUDIENCES.ELEVE];
  const Icon = cfg.icon || visual.icon;
  const accent = cfg.accent || visual.accent;
  const meta = AUDIENCE_META[audience];

  // Version compacte (1 ligne, sticky-friendly)
  if (compact) {
    return (
      <div
        className={`rounded-xl border-l-4 bg-card px-3 py-2 my-3 flex items-center gap-2.5 ${className}`}
        style={{ borderLeftColor: accent, background: `${accent}08` }}
        data-testid={`audience-banner-compact-${audience}`}
      >
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white flex-shrink-0"
          style={{ background: accent }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="text-[9px] font-black uppercase tracking-widest"
            style={{ color: accent }}
          >
            {visual.label}
          </span>
          <p className="text-xs sm:text-sm font-bold text-foreground leading-tight truncate">
            {cfg.title}
          </p>
        </div>
        {cfg.cta && cfg.ctaTo && (
          <Link
            to={cfg.ctaTo}
            className="text-xs font-black uppercase tracking-wider whitespace-nowrap hover:underline"
            style={{ color: accent }}
            data-testid={`audience-banner-cta-${audience}`}
          >
            {cfg.cta}
          </Link>
        )}
      </div>
    );
  }

  // Version standard
  return (
    <section
      className={`rounded-2xl border-2 bg-card overflow-hidden my-4 ${className}`}
      style={{ borderColor: `${accent}33` }}
      data-testid={`audience-banner-${audience}`}
    >
      <header
        className="px-4 sm:px-5 py-3 flex items-center gap-3"
        style={{ background: `${accent}0d` }}
      >
        <div
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0"
          style={{ background: accent }}
        >
          <Icon className="w-5 h-5 sm:w-5.5 sm:h-5.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] font-black uppercase tracking-widest"
            style={{ color: accent }}
          >
            {visual.label}
          </p>
          <h3
            className="text-base sm:text-lg font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {cfg.title}
          </h3>
        </div>
      </header>

      {(cfg.intro || cfg.bullets?.length > 0) && (
        <div className="px-4 sm:px-5 py-3">
          {cfg.intro && (
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-2">
              {cfg.intro}
            </p>
          )}
          {cfg.bullets?.length > 0 && (
            <ul className="space-y-1.5">
              {cfg.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-xs sm:text-sm text-foreground/90"
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: accent }}
                    aria-hidden="true"
                  />
                  <span className="leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          )}
          {cfg.cta && cfg.ctaTo && (
            <Link
              to={cfg.ctaTo}
              className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider mt-3 hover:underline"
              style={{ color: accent }}
              data-testid={`audience-banner-cta-${audience}`}
            >
              {cfg.cta} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      )}

      {meta?.homePath && (
        <footer
          className="px-4 sm:px-5 py-2 border-t flex items-center justify-between gap-3 text-[10px] text-muted-foreground"
          style={{ borderColor: `${accent}22`, background: `${accent}04` }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Info className="w-3 h-3" />
            Cette page est adaptée à ton profil&nbsp;: {meta.label}.
          </span>
          <Link
            to={meta.homePath}
            className="font-black uppercase tracking-wider hover:underline whitespace-nowrap"
            style={{ color: accent }}
            data-testid={`audience-banner-hub-${audience}`}
          >
            Mon hub →
          </Link>
        </footer>
      )}
    </section>
  );
}

// Export nommé pour faciliter la recherche
export { AudienceContextBanner };
